import React, { useState, useMemo, useCallback } from 'react'
import classNames from 'classnames'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { OnboardingIntegrationButton } from '../OnboardingIntegrationButton/OnboardingIntegrationButton'
import styles from './OnboardingIntegrationSelector.module.scss'
import { Button } from '../../../../components/Button/Button'

export interface OnboardingIntegrationSelectorProps {
  serviceIds: IntegrationServiceId[]
  onSelect: (serviceId: IntegrationServiceId) => void
  pageSize?: number
  showMoreThreshold?: number
  className?: string
}

export const OnboardingIntegrationSelector: React.FC<OnboardingIntegrationSelectorProps> = ({
  serviceIds,
  onSelect,
  pageSize,
  showMoreThreshold = 6,
  className,
}) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [showAll, setShowAll] = useState(false)

  const pages = useMemo<IntegrationServiceId[][]>(() => {
    if (pageSize === undefined || serviceIds.length <= pageSize) {
      return [serviceIds]
    }
    const result: IntegrationServiceId[][] = []
    for (let i = 0; i < serviceIds.length; i += pageSize) {
      result.push(serviceIds.slice(i, i + pageSize))
    }
    return result
  }, [serviceIds, pageSize])

  const isPaged = pages.length > 1
  const totalPages = pages.length
  const currentItems = pages[currentPage] ?? []

  const handlePrev = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), [])
  const handleNext = useCallback(() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1)), [totalPages])

  const hasMore = !isPaged && serviceIds.length > showMoreThreshold
  const visibleIds = hasMore && !showAll ? serviceIds.slice(0, showMoreThreshold) : serviceIds

  if (!isPaged) {
    return (
      <div className={classNames('d-flex flex-column align-items-center gap-3 w-100', className)}>
        <div className={classNames(styles.grid, 'd-flex flex-wrap gap-4 justify-content-center')}>
          {visibleIds.map((serviceId) => (
            <OnboardingIntegrationButton key={serviceId} serviceId={serviceId} onClick={() => onSelect(serviceId)} />
          ))}
        </div>
        {hasMore ? (
          <Button variant='primaryLinkButton' className={'button-medium text-decoration-none'} onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show less' : 'Show more'}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className={classNames('d-flex flex-column align-items-center gap-3 w-100', className)}>
      <div className={classNames(styles.carousel, 'd-flex align-items-center w-100')}>
        <Button variant='borderless' className='p-1 text-secondary' onClick={handlePrev} disabled={currentPage === 0} aria-label='Previous page'>
          <i className='bi bi-chevron-left' />
        </Button>
        <div className={classNames(styles.grid, 'd-flex flex-wrap gap-4 justify-content-start')}>
          {currentItems.map((serviceId) => (
            <OnboardingIntegrationButton key={serviceId} serviceId={serviceId} onClick={() => onSelect(serviceId)} />
          ))}
        </div>
        <Button variant='borderless' className='p-1 text-secondary' onClick={handleNext} disabled={currentPage === totalPages - 1} aria-label='Next page'>
          <i className='bi bi-chevron-right' />
        </Button>
      </div>
      <div className={classNames(styles.dots, 'd-flex align-items-center justify-content-center')}>
        {pages.map((_, i) => (
          <button
            key={i}
            type='button'
            className={classNames(styles.dot, 'rounded-circle border-0 p-0', {
              [styles.dotActive]: i === currentPage,
            })}
            onClick={() => setCurrentPage(i)}
            aria-label={`Go to page ${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
